import { Stack, Group, ActionIcon, Text, Button } from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react"
import dayjs from "dayjs"
import { t } from "i18next"
import { notifications } from "@mantine/notifications"
import { Page } from "../../../../../-components/Page"
import { dayJstoDateOnly } from "../../../../../utils/dayjs-to-date-only"
import { ActivityMappingTable } from "./activity-mapping-table"
import { CalculateRoyaltiesModal } from "./calculate-royalties-modal"
import { usePostMutation } from "../../../../../typed-client/hooks/use-post-mutation"
import { version } from "../../../../version/-version.json"

export function RoyaltiesPage() {
  const [selectedMonth, setSelectedMonth] = React.useState(new Date())
  const [calculateModalOpened, setCalculateModalOpened] = React.useState(false)
  const [tableRefreshKey, setTableRefreshKey] = React.useState(0)
  const [isDownloadingSettlement, setIsDownloadingSettlement] =
    React.useState(false)
  const { mutateAsync: updateSales, isPending: isUpdatingSales } =
    usePostMutation("/api/ActicitySale/UpdateSals")

  const start = React.useMemo(
    () => dayJstoDateOnly(dayjs(selectedMonth).startOf("month")),
    [selectedMonth]
  )
  const end = React.useMemo(
    () => dayJstoDateOnly(dayjs(selectedMonth).endOf("month")),
    [selectedMonth]
  )

  const year = selectedMonth.getFullYear()
  const month = selectedMonth.getMonth() + 1

  const selectedYearMonth = React.useMemo(
    () => ({
      year: selectedMonth.getFullYear(),
      month: selectedMonth.getMonth() + 1,
    }),
    [selectedMonth]
  )

  const handleUpdateSales = async () => {
    try {
      await updateSales({
        start,
        end,
        caption: "",
        totalDays: 0,
        isEmpty: false,
        isBaseOrEmpty: false,
      })

      notifications.show({
        title: t("Success"),
        message: t("UpdateSalesSuccess"),
        color: "green",
      })

      setTableRefreshKey((prev) => prev + 1)
    } catch (error) {
      notifications.show({
        title: t("UpdateSalesFailed"),
        message:
          error instanceof Error ? error.message : t("LinkFailedUnexpected"),
        color: "red",
      })
    }
  }

  const handleDownloadSettlement = async () => {
    setIsDownloadingSettlement(true)
    try {
      const response = await fetch(
        `/api/RoyaltiesSettlement/GetRoyaltiesSettlement?year=${year}&month=${month}`,
        {
          method: "GET",
          headers: {
            "Client-Version": version,
          },
        }
      )

      if (!response.ok) {
        throw new Error(t("DownloadSettlementFailed"))
      }

      const fileBlob = await response.blob()
      const objectUrl = window.URL.createObjectURL(fileBlob)
      const link = document.createElement("a")
      link.href = objectUrl
      link.download = `Royalties${year}-${String(month).padStart(2, "0")}.docx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      notifications.show({
        title: t("Error"),
        message:
          error instanceof Error
            ? error.message
            : t("DownloadFailedUnexpected"),
        color: "red",
      })
    } finally {
      setIsDownloadingSettlement(false)
    }
  }

  return (
    <Page>
      <Stack h="100%">
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              const prev = new Date(selectedMonth)
              prev.setMonth(prev.getMonth() - 1)
              setSelectedMonth(prev)
            }}
          >
            <IconArrowLeft />
          </ActionIcon>
          <MonthPickerInput
            value={selectedMonth}
            onChange={(v) => v && setSelectedMonth(dayjs(v).toDate())}
            w={160}
          />
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => {
              const next = new Date(selectedMonth)
              next.setMonth(next.getMonth() + 1)
              setSelectedMonth(next)
            }}
          >
            <IconArrowRight />
          </ActionIcon>
        </Group>
        <React.Suspense fallback={<Text c="dimmed">{t("Loading")}</Text>}>
          <ActivityMappingTable key={tableRefreshKey} start={start} end={end} />
        </React.Suspense>
        <Group justify="flex-end" mt="auto">
          <Button
            variant="default"
            onClick={handleDownloadSettlement}
            loading={isDownloadingSettlement}
          >
            {t("DownloadSettlement")}
          </Button>
          <Button
            variant="light"
            color="teal"
            onClick={handleUpdateSales}
            loading={isUpdatingSales}
          >
            {t("UpdateSales")}
          </Button>
          <Button color="teal" onClick={() => setCalculateModalOpened(true)}>
            {t("CalculateRoyalties")}
          </Button>
        </Group>

        <CalculateRoyaltiesModal
          opened={calculateModalOpened}
          onClose={() => setCalculateModalOpened(false)}
          yearMonth={selectedYearMonth}
        />
      </Stack>
    </Page>
  )
}
